// frontend/src/services/api.js  (subdomain-aware)
import axios from 'axios';
import { getApiBaseUrl, getSubdomain } from '../utils/subdomain';
import { enqueue } from '../utils/offlineDB';
import { useAccessStore } from '../store/accessStore';

// Mutations that are safe to queue and replay when back online.
// Payments and complex create operations are intentionally excluded.
const OFFLINE_QUEUEABLE = [
  { method: 'PATCH', re: /^\/housekeeping\// },
  // Checklist step toggles — must queue so required steps can be ticked offline,
  // which unblocks the final check-in/checkout confirm button.
  { method: 'PATCH', re: /^\/bookings\/[^/]+\/checklist\// },
  { method: 'PATCH', re: /^\/bookings\/[^/]+\/checkin/ },
  { method: 'PATCH', re: /^\/bookings\/[^/]+\/checkout/ },
  // Invoice preference + billing split — saved during check-in, safe to queue offline
  { method: 'PATCH', re: /^\/bookings\/[^/]+\/invoice-preference$/ },
  { method: 'PATCH', re: /^\/rooms\/[^/]+\/status/ },
  { method: 'PATCH', re: /^\/maintenance\// },
  { method: 'POST',  re: /^\/maintenance$/ },
  { method: 'POST',  re: /^\/pos\/orders$/ },
  { method: 'POST',  re: /^\/outlets\/orders$/ },
  { method: 'PATCH', re: /^\/outlets\/orders\// },
  { method: 'POST',  re: /^\/minibar\/checkout-review\/[^/]+\/confirm$/ },
  // Group check-in (status PATCH) and group checkout (single POST with payment)
  { method: 'PATCH', re: /^\/groups\/[^/]+\/status$/ },
  { method: 'POST',  re: /^\/groups\/[^/]+\/checkout$/ },
];

function isQueueable(method, url) {
  const m = (method || '').toUpperCase();
  return OFFLINE_QUEUEABLE.some(r => r.method === m && r.re.test(url));
}

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Offline queue interceptor (added BEFORE auth interceptor so it runs first) ─
api.interceptors.request.use(async (config) => {
  if (!navigator.onLine && !config._skipOffline && isQueueable(config.method, config.url)) {
    const idempotencyKey = crypto.randomUUID();
    try {
      await enqueue({
        idempotencyKey,
        method: config.method,
        url:    config.url,
        body:   config.data,
      });
      // Import lazily to avoid circular deps at module init time
      const { useOfflineStore } = await import('../store/offlineStore');
      useOfflineStore.getState().incrementPending();
    } catch (e) {
      // Queue write failed — let the request go through normally and fail
      return config;
    }
    // Cancel this request; response error interceptor converts it to synthetic success
    config._offlineQueued    = true;
    config._offlineKey       = idempotencyKey;
    config.signal            = AbortSignal.abort();
  }
  return config;
}, (error) => Promise.reject(error));

// ── Auth + subdomain headers ─────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const state = JSON.parse(localStorage.getItem('staynivo-auth') || '{}');
  const token = state?.state?.accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const subdomain = getSubdomain();
  if (subdomain) config.headers['X-Hotel-Subdomain'] = subdomain;
  return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Convert queued-offline cancellation into a synthetic success response
    if (error.config?._offlineQueued) {
      return {
        data:    { offline: true, queued: true, idempotencyKey: error.config._offlineKey },
        status:  202,
        headers: {},
        config:  error.config,
      };
    }

    const original = error.config;
    if (error.response?.status === 401 && !original._retry && error.response?.data?.code === 'TOKEN_EXPIRED') {
      original._retry = true;
      try {
        const { useAuthStore } = await import('../store/authStore');
        const refreshed = await useAuthStore.getState().refreshAccessToken();
        if (refreshed) return api(original);
      } catch (e) {}
    }
    // Cash-drawer rules reach many payment screens that show the server's
    // message in a toast — give it to them in the reader's language.
    const drawerCode = error.response?.data?.code || (error.response?.data?.message === 'DRAWER_REQUIRED' ? 'DRAWER_REQUIRED' : null);
    if (drawerCode && error.response?.data && (['DRAWER_REQUIRED', 'TENDER_TOO_LOW', 'INSUFFICIENT_CASH'].includes(drawerCode) || String(original?.url || '').includes('/cash-drawer'))) {
      try {
        const [{ default: i18n }, { drawerErrorText, DRAWER_ERROR_CODES }] = await Promise.all([import('../i18n'), import('../utils/cashDrawer')]);
        if (DRAWER_ERROR_CODES.includes(drawerCode)) error.response.data.message = drawerErrorText(i18n.t.bind(i18n), drawerCode);
      } catch (_) { /* keep the server's message */ }
    }
    if (error.response?.data?.code === 'WRONG_TENANT') {
      const { useAuthStore } = await import('../store/authStore');
      useAuthStore.getState().logout();
    }
    if (error.response?.status === 402) {
      const d = error.response?.data || {};
      if (d.code === 'TRIAL_EXPIRED' || d.code === 'SUBSCRIPTION_REQUIRED') {
        // Layout swaps the page for the "trial has ended" screen. (A redirect to
        // /subscription here looped: that page's own shell calls also got 402.)
        useAccessStore.getState().setBlocked({ code: d.code, trialEndsAt: d.trialEndsAt, graceEndsAt: d.graceEndsAt });
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// ─── HR / PAYROLL / ATTENDANCE ─────────────────────────────────
export const hrApi = {
  // Attendance
  getAttendance:    (params) => api.get('/hr/attendance', { params }),
  clockIn:          (data)   => api.post('/hr/attendance/clock-in', data),
  clockOut:         (data)   => api.post('/hr/attendance/clock-out', data),
  updateAttendance: (id, d)  => api.patch('/hr/attendance/'+id, d),
  getSummary:       (params) => api.get('/hr/attendance/summary', { params }),
  // Expenses
  getExpenses:        (p) => api.get('/hr/expenses', { params: p }),
  createExpense:      (d) => api.post('/hr/expenses', d),
  updateExpense:      (id,d) => api.patch('/hr/expenses/'+id, d),
  deleteExpense:      (id) => api.delete('/hr/expenses/'+id),
  getCategories:      ()  => api.get('/hr/expenses/categories'),
  // Payroll
  getPayrollPeriods:  ()      => api.get('/hr/payroll'),
  generatePayroll:    (d)     => api.post('/hr/payroll/generate', d),
  getPayrollItems:    (id)    => api.get('/hr/payroll/'+id+'/items'),
  approvePayroll:     (id)    => api.patch('/hr/payroll/'+id+'/approve'),
  markPaid:           (id)    => api.patch('/hr/payroll/'+id+'/paid'),
  getSalaryStructure: (uid)   => api.get('/hr/salary/'+uid),
  saveSalaryStructure:(uid,d) => api.put('/hr/salary/'+uid, d),
  // Leave
  getLeaveTypes:        ()       => api.get('/hr/leave/types'),
  createLeaveType:      (d)      => api.post('/hr/leave/types', d),
  updateLeaveType:      (id,d)   => api.patch('/hr/leave/types/'+id, d),
  deleteLeaveType:      (id)     => api.delete('/hr/leave/types/'+id),
  getLeaveRequests:     (p)      => api.get('/hr/leave/requests', { params: p }),
  createLeaveRequest:   (d)      => api.post('/hr/leave/requests', d),
  reviewLeave:          (id,d)   => api.patch('/hr/leave/requests/'+id, d),
  withdrawLeave:        (id,d)   => api.post('/hr/leave/requests/'+id+'/withdraw', d),
  reviewWithdrawal:     (id,d)   => api.patch('/hr/leave/requests/'+id+'/review-withdrawal', d),
  getLeaveSettings:     ()       => api.get('/hr/leave/settings'),
  saveLeaveSettings:    (d)      => api.patch('/hr/leave/settings', d),
  exportLeaveReport:    (p)      => api.get('/hr/leave/export', { params: p, responseType: 'blob' }),
  getLeaveReportJson:   (p)      => api.get('/hr/leave/export', { params: { ...p, format: 'json' } }),
  // P&L
  getPL:              (p)   => api.get('/hr/pl', { params: p }),
};

// ─── FINGERPRINT DEVICES ─────────────────────────────────────────
export const fingerprintApi = {
  getDevices:       ()      => api.get('/fingerprint'),
  addDevice:        (d)     => api.post('/fingerprint', d),
  updateDevice:     (id,d)  => api.patch('/fingerprint/'+id, d),
  deleteDevice:     (id)    => api.delete('/fingerprint/'+id),
  testConnection:   (d)     => api.post('/fingerprint/test', d),
  syncDevice:       (id)    => api.post('/fingerprint/'+id+'/sync'),
  syncAll:          ()      => api.post('/fingerprint/sync-all'),
  enrollUser:       (devId,uid) => api.post('/fingerprint/'+devId+'/enroll/'+uid),
  getEnrolled:      (id)    => api.get('/fingerprint/'+id+'/users'),
  getTemplates:     ()      => api.get('/fingerprint/shift-templates'),
  createTemplate:   (d)     => api.post('/fingerprint/shift-templates', d),
};

// ─── SHIFTS (enhanced) ───────────────────────────────────────────
export const shiftsApi = {
  getWeek:          (p)     => api.get('/staff/shifts/week', { params: p }),
  assignBulk:       (d)     => api.post('/staff/shifts/bulk', d),
  update:           (id,d)  => api.patch('/staff/shifts/'+id, d),
  delete:           (id)    => api.delete('/staff/shifts/'+id),
  acknowledge:      (id)    => api.patch('/staff/shifts/'+id+'/acknowledge'),
};

// ─── FACILITIES ──────────────────────────────────────────────────
export const facilitiesApi = {
  list:           ()       => api.get('/facilities'),
  get:            (id)     => api.get('/facilities/'+id),
  create:         (d)      => api.post('/facilities', d),
  update:         (id,d)   => api.patch('/facilities/'+id, d),
  remove:         (id)     => api.delete('/facilities/'+id),
  dashboard:      (id)     => api.get('/facilities/'+id+'/dashboard'),
  expenses:       (id,p)   => api.get('/facilities/'+id+'/expenses', { params:p }),
  // Tasks
  getTasks:       (p)      => api.get('/facilities/tasks/all', { params:p }),
  createTask:     (d)      => api.post('/facilities/tasks', d),
  updateTask:     (id,d)   => api.patch('/facilities/tasks/'+id, d),
  tickItem:       (id,d)   => api.post('/facilities/tasks/'+id+'/checklist', d),
  taskClock:      (id,d)   => api.post('/facilities/tasks/'+id+'/clock', d),
  // Duty roster
  getDuty:        (p)      => api.get('/facilities/duty/roster', { params:p }),
  assignDuty:     (d)      => api.post('/facilities/duty', d),
  updateDuty:     (id,d)   => api.patch('/facilities/duty/'+id, d),
  // Incidents
  getIncidents:   (p)      => api.get('/facilities/incidents/all', { params:p }),
  createIncident: (d)      => api.post('/facilities/incidents', d),
  resolveIncident:(id,d)   => api.patch('/facilities/incidents/'+id+'/resolve', d),
  // Logs
  getLogs:        (p)      => api.get('/facilities/logs/all', { params:p }),
  addLog:         (d)      => api.post('/facilities/logs', d),
};

// ─── OUTLETS ─────────────────────────────────────────────────────
export const outletsApi = {
  list:           ()         => api.get('/outlets'),
  create:         (d)        => api.post('/outlets', d),
  update:         (id,d)     => api.patch('/outlets/'+id, d),
  // Menu
  getMenu:        (outletId) => api.get('/outlets/'+outletId+'/menu'),
  createCategory: (d)        => api.post('/outlets/menu/categories', d),
  createItem:     (d)        => api.post('/outlets/menu/items', d),
  updateItem:     (id,d)     => api.patch('/outlets/menu/items/'+id, d),
  // Orders
  getOrders:      (p)        => api.get('/outlets/orders', { params:p }),
  createOrder:    (d)        => api.post('/outlets/orders', d),
  updateOrder:    (id,d)     => api.patch('/outlets/orders/'+id, d),
  // Revenue
  getRevenue:     (p)        => api.get('/outlets/revenue', { params:p }),
};

// ─── ACTIVITIES ───────────────────────────────────────────────────
export const activitiesApi = {
  listTypes:      (p)     => api.get('/activities/types', { params:p }),
  createType:     (d)     => api.post('/activities/types', d),
  updateType:     (id,d)  => api.patch('/activities/types/'+id, d),
  getSessions:    (p)     => api.get('/activities/sessions', { params:p }),
  createSession:  (d)     => api.post('/activities/sessions', d),
  getSession:     (id)    => api.get('/activities/sessions/'+id),
  updateSession:  (id,d)  => api.patch('/activities/sessions/'+id, d),
  getReceipt:     (id)    => `/activities/sessions/${id}/receipt`,
  listEquipment:  (p)     => api.get('/activities/equipment', { params:p }),
  createEquipment:(d)     => api.post('/activities/equipment', d),
  updateEquipment:(id,d)  => api.patch('/activities/equipment/'+id, d),
  getRevenue:     (p)     => api.get('/activities/revenue', { params:p }),
};

// ─── REVENUE MANAGEMENT ──────────────────────────────────────────
export const revenueApi = {
  suggest:       (p)    => api.get('/revenue/suggest',       { params:p }),
  calendar:      (p)    => api.get('/revenue/calendar',      { params:p }),
  metrics:       (p)    => api.get('/revenue/metrics',       { params:p }),
  getRules:      ()     => api.get('/revenue/rules'),
  updateRules:   (d)    => api.put('/revenue/rules', d),
  listRatePlans: ()     => api.get('/revenue/rate-plans'),
  createRatePlan:(d)    => api.post('/revenue/rate-plans', d),
  updateRatePlan:(id,d) => api.patch('/revenue/rate-plans/'+id, d),
  setOverride:   (d)    => api.post('/revenue/calendar/override', d),
  deleteOverride:(p)    => api.delete('/revenue/calendar/override', { params:p }),
};

// ─── GROUPS ──────────────────────────────────────────────────────
export const groupsApi = {
  list:          (p)    => api.get('/groups', { params:p }),
  get:           (id)   => api.get('/groups/'+id),
  create:        (d)    => api.post('/groups', d),
  createV2:      (d)    => api.post('/groups/v2', d),
  update:        (id,d) => api.patch('/groups/'+id, d),
  changeStatus:  (id,d) => api.patch('/groups/'+id+'/status', d),
  assignRoom:    (id,d) => api.post('/groups/'+id+'/rooms', d),
  stats:         ()     => api.get('/groups/stats'),
  // Rate preview
  ratePreview:   (p)    => api.get('/groups/rate-preview', { params:p }),
  // Deposit
  payDeposit:    (id, instId, d) => api.post(`/groups/${id}/deposit/${instId}/pay`, d),
  // Room release
  releaseRooms:  (id, d) => api.post('/groups/'+id+'/rooms/release', d),
  // Attrition
  attrition:     (id)   => api.get('/groups/'+id+'/attrition'),
  // Waitlist
  listWaitlist:  (id)   => api.get('/groups/'+id+'/waitlist'),
  addWaitlist:   (id,d) => api.post('/groups/'+id+'/waitlist', d),
  notifyWaitlist:(id)   => api.post('/groups/'+id+'/waitlist/notify'),
  // Amendments
  createAmendment:  (id,d)        => api.post('/groups/'+id+'/amendments', d),
  approveAmendment: (id,amendId)  => api.post(`/groups/${id}/amendments/${amendId}/approve`),
  // Seasonal periods
  listPeriods:   (p)    => api.get('/groups/seasonal/periods', { params:p }),
  createPeriod:  (d)    => api.post('/groups/seasonal/periods', d),
  updatePeriod:  (id,d) => api.patch('/groups/seasonal/periods/'+id, d),
  deletePeriod:  (id)   => api.delete('/groups/seasonal/periods/'+id),
};

// ─── BACKUP ───────────────────────────────────────────────────────
export const backupApi = {
  downloadBackup: ()        => api.post('/backup/download', {}, { responseType:'blob' }),
  exportCSV:      (type, p) => api.get('/backup/export/csv/'+type, { params:p, responseType:'blob' }),
  getHistory:     ()        => api.get('/backup/history'),
  restore:        (d)       => api.post('/backup/restore', d),
};

export default api;

export const dashboardApi = { get: () => api.get('/dashboard') };

export const bookingsApi = {
  list: (params) => api.get('/bookings', { params }),
  get: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  checkIn: (id, body = {}) => api.patch(`/bookings/${id}/checkin`, body),
  checkOut: (id) => api.patch(`/bookings/${id}/checkout`),
  cancel: (id, reason) => api.patch(`/bookings/${id}/cancel`, { reason }),
  checkAvailability: (params) => api.get('/rooms/availability', { params }),
};

export const roomsApi = {
  list: (params) => api.get('/rooms', { params }),
  updateStatus: (id, status) => api.patch(`/rooms/${id}/status`, { status }),
  create: (data) => api.post('/rooms', data),
};

export const guestsApi = {
  list: (params) => api.get('/guests', { params }),
  get: (id) => api.get(`/guests/${id}`),
  create: (data) => api.post('/guests', data),
  update: (id, data) => api.patch(`/guests/${id}`, data),
};

export const housekeepingApi = {
  list: (params) => api.get('/housekeeping', { params }),
  create: (data) => api.post('/housekeeping', data),
  update: (id, data) => api.patch(`/housekeeping/${id}`, data),
};

export const maintenanceApi = {
  list: () => api.get('/maintenance'),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.patch(`/maintenance/${id}`, data),
};

export const posApi = {
  menu: () => api.get('/pos/menu'),
  createOrder: (data) => api.post('/pos/orders', data),
  orders: (date) => api.get('/pos/orders' + (date ? `?date=${date}` : '')),
};

export const inventoryApi = {
  list: () => api.get('/inventory'),
  updateStock: (id, data) => api.patch(`/inventory/${id}/stock`, data),
};

export const staffApi = {
  list: () => api.get('/staff'),
  shifts: (date) => api.get('/staff/shifts', { params: { date } }),
};

export const notificationsApi = {
  list: () => api.get('/notifications'),
  vapidKey: () => api.get('/notifications/vapid-key'),
  subscribe: (sub) => api.post('/notifications/subscribe', sub),
  unsubscribe: (data) => api.delete('/notifications/subscribe', { data }),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  deleteOne: (id) => api.delete(`/notifications/${id}`),
  deleteAll: () => api.delete('/notifications'),
};

export const eventsApi = {
  list: () => api.get('/events'),
  venues: () => api.get('/venues'),
};

export const spaApi = {
  services: () => api.get('/spa/services'),
  appointments: (date) => api.get('/spa/appointments', { params: { date } }),
};

export const paymentsApi = { create: (data) => api.post('/payments', data) };

export const hotelApi = {
  get: () => api.get('/hotel'),
  info: () => api.get('/hotel-info'),
  updateModules: (modules) => api.patch('/hotel/modules', { modules }),
};

export const reportsApi = {
  revenue:           (params) => api.get('/reports/revenue',             { params }),
  occupancy:         (params) => api.get('/reports/occupancy',           { params }),
  guests:            (params) => api.get('/reports/guests',              { params }),
  summary:           (params) => api.get('/reports/summary',             { params }),
  groups:            (params) => api.get('/reports/groups',              { params }),
  revenueVsExpenses: (params) => api.get('/reports/revenue-vs-expenses', { params }),
  profitLoss:        (params) => api.get('/reports/profit-loss',         { params }),
  ota:               (params) => api.get('/reports/ota',                  { params }),
  cancellations:     (params) => api.get('/reports/cancellations',         { params }),
};

// Super admin API client (no subdomain context needed)
const superApi = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api','') + '/api/superadmin',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});
superApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('staynivo-superadmin-token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
// Strip the /superadmin prefix from paths so callers can use full paths
// like superAdminApiClient.get('/superadmin/settings') and hit /api/superadmin/settings
const stripPrefix = (path) => path.replace(/^\/superadmin/, '');

export const superAdminApiClient = {
  // Named convenience methods
  login: (data) => superApi.post('/login', data),
  stats: () => superApi.get('/stats'),
  twofa: {
    completeLogin: (data) => superApi.post('/2fa/complete-login', data),
    status:        ()     => superApi.get('/2fa/status'),
    setup:         ()     => superApi.post('/2fa/setup'),
    verifySetup:   (data) => superApi.post('/2fa/verify-setup', data),
    disable:       (data) => superApi.post('/2fa/disable', data),
  },
  hotels: {
    list: () => superApi.get('/hotels'),
    get: (id) => superApi.get(`/hotels/${id}`),
    create: (data) => superApi.post('/hotels', data),
    update: (id, data) => superApi.patch(`/hotels/${id}`, data),
    deactivate: (id) => superApi.delete(`/hotels/${id}`),
    setCustomDomain: (id, domain) => superApi.post(`/hotels/${id}/custom-domain`, { customDomain: domain }),
    checkSubdomain: (sub) => superApi.get(`/subdomains/check?subdomain=${sub}`),
    getDb:      (id)       => superApi.get(`/hotels/${id}/db`),
    setDb:      (id, data) => superApi.put(`/hotels/${id}/db`, data),
    testDb:     (id, data) => superApi.post(`/hotels/${id}/db/test`, data),
    migrateDb:  (id)       => superApi.post(`/hotels/${id}/db/migrate`),
  },
  // Generic HTTP methods — path may include /superadmin prefix, which is stripped
  get:   (path, config)       => superApi.get(stripPrefix(path), config),
  post:  (path, data, config) => superApi.post(stripPrefix(path), data, config),
  patch: (path, data, config) => superApi.patch(stripPrefix(path), data, config),
  put:   (path, data, config) => superApi.put(stripPrefix(path), data, config),
  delete:(path, config)       => superApi.delete(stripPrefix(path), config),
};

// Direct API client using superadmin token — for routes outside /api/superadmin (e.g. /api/blog/...)
const superApiDirect = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});
superApiDirect.interceptors.request.use((config) => {
  const token = localStorage.getItem('staynivo-superadmin-token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
export const superAdminDirectApi = superApiDirect;

export const guestEmailApi = {
  getTemplates:   ()         => api.get('/guests/email-templates'),
  createTemplate: (data)     => api.post('/guests/email-templates', data),
  updateTemplate: (id, data) => api.patch(`/guests/email-templates/${id}`, data),
  deleteTemplate: (id)       => api.delete(`/guests/email-templates/${id}`),
  sendEmail:      (data)     => api.post('/guests/send-email', data),
  getLogs:        ()         => api.get('/guests/email-logs'),
  uploadEmailImages: (files) => {
    const fd = new FormData();
    files.forEach(f => fd.append('images', f));
    return api.post('/guests/email-images', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadDocument: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/guests/${id}/document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};
